import {
  AssetOptionsContext,
  type AssetOptionsContextProps,
  DEFAULT_MIN_SVG_WIDTH,
} from '@contexts/asset-options'
import { ContentType } from '@entries/content-types'
import ContentfulAsset from '@entries/contentful-asset'
import { withDisplayName } from '@hocs/with-display-name'
import { Entry } from '@resolvers/entry'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { contentArticleTitleOptions } from '@rich-text/contentful-content-article-rich-text'
import { Card } from '@ui/card/card'
import { CtaLink } from '@ui/cta-link'
import {
  ENHANCED_COMPONENT_ROLE,
  ENHANCED_COMPONENT_TYPE,
} from '@ui/enhanced-components/types'
import { graphql } from 'gatsby'
import React from 'react'

const assetOptions: AssetOptionsContextProps = {
  width: '100%',
  height: '100%',
  minSvgWidth: DEFAULT_MIN_SVG_WIDTH,
  maxSvgHeight: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  alt: '',
}

const ContentfulContentArticleCardImageFull: Utils.EntryFC<
  Queries.ContentfulContentArticleCardImageFullFragment
> = ({
  content: {
    image,
    contentArticleTitle: title,
    subtitle,
    content,
    contentLinkText,
    contentLinkUrl,
  },
  fieldInspectorProps,
}) => {
  const hasHeader = title || subtitle
  const hasFooterLink = contentLinkText && contentLinkUrl
  const isClickable = !contentLinkText && contentLinkUrl

  return (
    <Card href={contentLinkUrl} isClickable={Boolean(isClickable)}>
      {image && (
        <div
          className="h-[230px] min-h-[230px] w-full"
          {...fieldInspectorProps({
            fieldId: 'image',
          })}
        >
          <AssetOptionsContext.Provider value={assetOptions}>
            <Entry
              content={image}
              mapping={{
                [ContentType.ContentfulAsset]: ContentfulAsset,
              }}
            />
          </AssetOptionsContext.Provider>
        </div>
      )}
      <div className="flex h-full flex-col justify-between px-10 pb-10 pt-5">
        {hasHeader && (
          <div>
            {title && (
              <RichText
                {...fieldInspectorProps({
                  fieldId: 'title',
                })}
                content={title}
                options={contentArticleTitleOptions}
              />
            )}
            {subtitle && (
              <div className="pb-5">
                <RichText
                  {...fieldInspectorProps({
                    fieldId: 'subtitle',
                  })}
                  content={subtitle}
                  options={defaultRichTextFieldOptionsDeprecated}
                />
              </div>
            )}
            {content && (
              <RichText
                {...fieldInspectorProps({
                  fieldId: 'content',
                })}
                content={content}
                options={defaultRichTextFieldOptionsDeprecated}
              />
            )}
          </div>
        )}
        {hasFooterLink && (
          <div className="mt-10 flex justify-center">
            <CtaLink
              data-role={ENHANCED_COMPONENT_ROLE.SECONDARY}
              data-type={ENHANCED_COMPONENT_TYPE.CTA}
              href={contentLinkUrl}
              {...fieldInspectorProps({
                fieldId: 'contentLinkText',
              })}
            >
              {contentLinkText}
            </CtaLink>
          </div>
        )}
      </div>
    </Card>
  )
}

export const query = graphql`
  fragment ContentfulContentArticleCardImageFull on Contentful4TmD9EihHakmKMkEwKaUwQ {
    ...ContentfulEntry
    contentArticleTitle: title {
      ...ContentfulContentArticleTitle
    }
    subtitle {
      ...ContentfulContentArticleSubtitle
    }
    content {
      ...ContentfulContentArticleContent
    }
    image {
      ...ContentfulDefaultAsset
    }
    contentLinkText
    contentLinkUrl
  }
`

export default withDisplayName(
  ContentfulContentArticleCardImageFull,
  'ContentfulContentArticleCardImageFull'
)
